import React, { useState, useEffect, useRef }  from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import TechnicalPropertyOptionRow from 'components/show_drawing/redraw_form/technical_properties/TechnicalPropertyOptionRow.jsx';
import styles from 'components/show_drawing/redraw_form/_style.module.scss';
import Constants from 'components/show_drawing/Constants.jsx';

// Lists the drawing options an admin flagged as technical properties
// (DrawingOption#technical_property) in a popup that deliberately looks and
// feels identical to the tag-based technical-properties popup
// (TechnicalProperties.jsx) — same wide two-column modal, "Technical
// properties" title, row list and "Confirm technical properties" button — so
// the customer sees one familiar popup, not two different ones.
//
// Unlike a normal option, picking a value here does NOT redraw the dieline:
// the rows stage their choice in the form (redrawOnChange is off) but the
// main drawing only recomputes when "Confirm technical properties" is pressed
// — matching the tag popup, where the choice is a candidate until confirmed.
// Closing with the × instead reverts each option to its committed value
// (from currentDrawingOptions), so a cancelled pick cannot leak into a later
// redraw.
//
// The ContentPopup is mounted whenever any such option exists; opening only
// toggles its display (popup.css .modal is display:none by default). It stays
// mounted while closed on purpose: its inputs must remain inside
// <form ref={formRef}> so every redraw keeps sending the committed technical
// properties, even when the popup is shut.
//
// The right-hand preview is a placeholder SVG for now; wiring the live preview
// is a follow-up.
const TechnicalPropertyOptions = ({ id, drawing, unit, options, optionsUserChanged, addToOptionsUserChanged, setRedrawRequested, currentDrawingOptions }) => {
  const popupRef = useRef();
  const [isOpen, setIsOpen] = useState(false);

  // Match the tag popup's modal geometry so the two are indistinguishable.
  const containerWidth = $('.container').width?.();
  const modalStyle = {
    width: containerWidth ? containerWidth * 0.92 : undefined,
    height: "96%",
    maxWidth: "unset"
  };

  // Mirrors TechnicalProperties.jsx#resize: keep the scrollable left column and
  // the flex row the height of the modal body. Scoped to this popup and guarded
  // so it is a no-op where jQuery is absent (tests) or the popup is closed.
  useEffect(() => {
    if (!isOpen) { return; }
    let timer;
    const resize = () => {
      try {
        const modalContentHeight = $(popupRef.current).find('.modal-content').height();
        if (modalContentHeight) {
          $('#technical_property_options').height(modalContentHeight - 40);
          $('#technical_property_options_left').height(modalContentHeight - 40);
        }
      } catch (error) { /* no jQuery / not rendered */ }
      timer = setTimeout(resize, 200);
    };
    resize();
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (options.length == 0) { return <></> }

  const setDisplay = (value) => {
    if (popupRef.current) { popupRef.current.style.display = value; }
  }

  const committedValueOf = (option) => {
    if (currentDrawingOptions && currentDrawingOptions[option.name] != null) {
      return currentDrawingOptions[option.name];
    }
    return option.default_value;
  }

  const open = (e) => {
    e.preventDefault();
    setDisplay("block");
    setIsOpen(true);
  }

  // Commit the staged choices: the rows already hold them in the form, so one
  // redraw applies them all at once.
  const confirm = (e) => {
    e.preventDefault();
    setRedrawRequested(() => true);
    setDisplay("none");
    setIsOpen(false);
  }

  // Cancel: put every option back to its committed value without a redraw, so a
  // staged-but-unconfirmed pick does not survive the close.
  const cancel = () => {
    options.forEach((option) => {
      emitEvent(Constants.drawing_option_set_value, { id: id, name: option.name, value: committedValueOf(option), redraw: false });
    });
    setIsOpen(false);
  }

  const content = (
    <div className={styles.tp_div} id="technical_property_options">
      <div className={styles.tp_left} id="technical_property_options_left">
        <div className={styles.tp_ags_as_options}>
          <div className={styles.tp_header}> <h4> {t('models.technical_properties')} </h4> </div>

          {options.map((option, index) => (
            <TechnicalPropertyOptionRow
              key={`${id}${option.name}${unit}`}
              id={id}
              drawing={drawing}
              option={option}
              unit={unit}
              optionsUserChanged={optionsUserChanged}
              addToOptionsUserChanged={addToOptionsUserChanged}
              setRedrawRequested={setRedrawRequested}
              isLast={index === options.length - 1}
            />
          ))}

          <br />
        </div>

        <center>
          <div className={styles.row}>
            <button onClick={confirm} className="btn simple-btn">
              &nbsp; {t('models.confirm_technical_properties')}
            </button>
            <br />
            <br />
          </div>
        </center>
      </div>

      <div className={styles.tp_right}>
        <div className="redraw_theme_black" style={{ minHeight: '600px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}>
          <svg viewBox="0 0 400 300" width="60%" preserveAspectRatio="xMidYMid meet">
            <rect x="20" y="20" width="360" height="260" rx="8" fill="none" stroke="#3a3a3a" strokeWidth="2" strokeDasharray="10 8" />
            <line x1="20" y1="150" x2="380" y2="150" stroke="#2c2c2c" strokeWidth="1" />
            <line x1="200" y1="20" x2="200" y2="280" stroke="#2c2c2c" strokeWidth="1" />
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`${styles.row} ${styles.optionLink}`}>
        <span className={`input-group-addon input-group-addon-${window.locale} ${styles.input_add_on_for_technical_props}`}>
          <a href="#" onClick={open} className="btn simple-btn dieline-outline-btn">
            <i className="fa fa-pencil-square-o"></i> &nbsp; { t('models.change_technical_properties') }
          </a>
        </span>
      </div>

      <ContentPopup
        id="technical_property_options_modal"
        autoOpen={false}
        myRef={popupRef}
        content={content}
        cancelButton={false}
        beforeClose={cancel}
        modalStyle={modalStyle}
        notWide={true}
      />
    </>
  )
}

export default TechnicalPropertyOptions;
